/* TrialExpiredBanner — auto-prompts users whose Pro trial has ended.
 *
 * Shows a one-tap "Renew · ₹49" banner the FIRST time per day when:
 *   • user is signed in
 *   • trial_used == true (they had a trial — fresh signups get a 7-day trial,
 *     so this means it's been used up)
 *   • is_pro == false (currently free)
 *
 * Dismissed banners are remembered in localStorage for 24h so we don't
 * nag on every reload.
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Crown, Sparkle } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import PaywallSheet from "@/components/v2/PaywallSheet";

const DISMISS_KEY = "nowcast.trialExpired.dismissedAt.v1";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000;

function readDismissedAt() {
  try {
    const v = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    return Number.isFinite(v) ? v : 0;
  } catch {
    return 0;
  }
}

function writeDismissedAt(ts) {
  try {
    localStorage.setItem(DISMISS_KEY, String(ts));
  } catch {
    /* quota — ignore */
  }
}

export default function TrialExpiredBanner({ theme }) {
  const { user, isPro } = useAuth();
  const [open, setOpen] = useState(false);
  const [paywall, setPaywall] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (isPro) return;
    if (!user.trial_used) return;
    const dismissedAt = readDismissedAt();
    if (Date.now() - dismissedAt < DISMISS_TTL_MS) return;
    // Slight stagger so the banner doesn't compete with location-prompt etc.
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, [user, isPro]);

  const dismiss = () => {
    writeDismissedAt(Date.now());
    setOpen(false);
  };

  const upgrade = () => {
    setOpen(false);
    setTimeout(() => setPaywall(true), 220);
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed left-0 right-0 z-[60] flex justify-center px-3"
            style={{ top: "calc(env(safe-area-inset-top) + 56px)" }}
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -50, opacity: 0 }}
            transition={{ type: "spring", damping: 22, stiffness: 240 }}
            data-testid="trial-expired-banner"
          >
            <div
              className="w-full max-w-[420px] rounded-2xl px-3.5 py-3 flex items-center gap-2.5"
              style={{
                background: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
                border: "1px solid rgba(245,158,11,0.45)",
                boxShadow: "0 12px 28px rgba(245,158,11,0.18)",
              }}
            >
              <span
                className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ background: "rgba(245,158,11,0.22)", color: "#b45309" }}
              >
                <Crown size={16} weight="fill" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-bold leading-tight" style={{ color: "#451a03" }}>
                  Your Pro trial has ended
                </div>
                <div className="text-[10.5px] leading-snug mt-0.5" style={{ color: "#92400e" }}>
                  Atmospheric Pulse + full Report are locked. Keep them for ₹49/mo.
                </div>
              </div>
              <button
                type="button"
                onClick={upgrade}
                data-testid="trial-banner-renew"
                className="shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.1em] active:scale-95"
                style={{
                  background: "linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)",
                  color: "#0f172a",
                  boxShadow: "0 4px 12px rgba(245,158,11,0.45)",
                }}
              >
                <Sparkle size={9} weight="fill" className="inline mr-1 -mt-0.5" />
                Renew
              </button>
              <button
                type="button"
                onClick={dismiss}
                aria-label="Dismiss"
                data-testid="trial-banner-dismiss"
                className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center active:scale-95"
                style={{ background: "rgba(15,23,42,0.06)", color: "#78350f" }}
              >
                <X size={11} weight="bold" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <PaywallSheet open={paywall} onClose={() => setPaywall(false)} theme={theme} />
    </>
  );
}
